// @ts-nocheck

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SafeDeferredRender, { RenderMode } from 'app/shared/modules/SafeDeferredRender';

import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

import FavoriteButton from 'app/shared/modules/FavoriteButton';
import LazyLoader from '../../LazyLoader';
import Carousel from './Carousel';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';
import NewUnitBadge from 'app/shared/modules/hdp/NewUnitBadge';
import PriceDropBadge from 'app/shared/modules/hdp/PriceDropBadge';

import Icon3DGlasses from 'images/icons/3d-glasses.svg';
import IconVerified from 'images/verified.svg';
import SkeletonCard from './skeleton';
import noPhoto from 'images/noPhoto.png';

import { listingUtils_checkFavorite, listingUtils_formatPropertyType } from 'app/shared/utils/listingUtils';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import { useYieldCallback } from '@zillow/yield-callback';
import experimentConstants from 'app/shared/constants/ExperimentConstants';

import perfUtils from 'app/shared/utils/perfUtils';
import {
  badgeContainerCX,
  badgeOutlineCX,
  detailedInfoTextCX,
  getDetailedInfoContainerCX,
  getHeroPhotoContainerCX,
  getInfoBadgeCX,
  getListingCardContainerCX,
  infoBadgesCX,
  listingCardAnchorCX,
  priceTextCX,
} from './styles';
import { useListing } from '../ListingCardProvider';
import {
  emitTrackSaveHomeClickEvent,
  emitTrackUnSaveHomeClickEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';

const ListingCardV3 = ({
  isCarouselCard = false,
  listingViewed = false,
  keepMapLocation = false,
  onClick = () => {},
  shouldLazyLoadListingCardPhotos = false,
  shouldDeferRender = false,
  isApplyInstantlyExperiment = false,
  highPriority = false,
  isSpecialOffersExperiment = false,
}) => {
  const { listing, triggerLocation, triggerObject } = useListing();
  const dispatch = useDispatch();
  const screenWidth = useSelector((state: any) => state.app.device.screenWidth);
  const newUnitsExperiment = useSelector(
    (state: any) => state?.analytics?.runningExperiments?.[experimentConstants.HOTPADS_WEB_NEW_UNITS] || {},
  );
  const isNewUnitsControl = newUnitsExperiment?.isControl !== false;
  const priceDropExperiment = useSelector(
    (state: any) => state?.analytics?.runningExperiments?.[experimentConstants.HOTPADS_WEB_PRICE_DROPS] || {},
  );
  const isPriceDropControl = priceDropExperiment?.isControl !== false;
  const {
    active,
    address,
    averageStarLevel,
    displayName,
    has3dTour,
    hasSpecialOffers,
    incomeRestricted,
    listingMinMaxPriceBeds,
    medPhotoUrls,
    newUnitCount,
    priceDropCount,
    previewPhotoMed,
    recencyTime,
    rentalApplicationStatus,
    modelTypeUnitCount,
    seniorHousing,
    studentHousing,
    trusted,
    propertyType,
    specialOfferLabel,
  } = listing;
  const formattedPropertyType = listingUtils_formatPropertyType(propertyType);
  const isFavorited = listing ? listingUtils_checkFavorite(listing) : false;

  // Determine if card width will be < 400px
  // Desktop: carousel cards only (286px/300px)
  // Mobile: both SRP cards and carousel cards (both < 400px when screenWidth is 'sm' or 'xs')
  const isMobile = screenWidth === 'sm' || screenWidth === 'xs';
  const shouldUseNewLayout = shouldShowTotalMonthlyPriceBadge(listing) && (isCarouselCard || isMobile);

  const yieldFavoriteClick = useYieldCallback(() => {
    if (!isFavorited) {
      emitTrackSaveHomeClickEvent({ listing, triggerLocation, triggerObject });
    } else {
      emitTrackUnSaveHomeClickEvent({ listing, triggerLocation, triggerObject });
    }

    dispatch(UserItemActions.toggleUserItem('favorite', listing));
  });

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    yieldFavoriteClick();
  };

  const yieldListingClickDispatches = useYieldCallback((isTransitioning = true) => {
    onClick(listing);
    perfUtils.setMarker('ListingCardV3Click');
    dispatch(AppActions.setAreaPageScrollPosition(listing.maloneLotIdEncoded));
    dispatch(ListingEngineActions.setPreviewListing(listing));
    dispatch(ListingEngineActions.setActiveMarkerMaloneLotId(listing.maloneLotIdEncoded));
    if (listing?.uriV2 && isTransitioning) {
      dispatch(RouteActions.transitionToListing({ listingUri: listing.uriV2, keepMapLocation }));
    }
  });

  const handleListingClick = useCallback(
    (e, isTransitioning = true) => {
      if (e) {
        // Check if click originated from tooltip trigger or tooltip content
        const target = e.target as HTMLElement;
        const clickedTooltipTrigger = target.closest('[data-testid="listing-price-tooltip"]');
        const clickedTooltipContent = target.closest('[role="tooltip"]');
        const clickedTotalPriceBadge = target.closest('[data-testid="listing-price-tooltip"]');

        if (clickedTooltipTrigger || clickedTooltipContent || clickedTotalPriceBadge) {
          e.preventDefault();
          e.stopPropagation();
          return; // Don't navigate to listing
        }

        e.preventDefault();
        e.stopPropagation();
      }

      yieldListingClickDispatches(isTransitioning);
    },
    [yieldListingClickDispatches],
  );

  if (!listing) {
    return null;
  }

  if (previewPhotoMed?.url && medPhotoUrls.length === 0) {
    medPhotoUrls.push(previewPhotoMed.url);
  }

  const isBrokerListing = listing?.broker?.isBrokerExclusiveListing || false;
  const brokerName = listing?.broker?.companyName || '';
  const displayBrokerInfo = isBrokerListing && brokerName;

  const FavoriteButtonComponent = (
    <div style={{ position: 'absolute', top: '5px', right: '0px', zIndex: '1' }}>
      <FavoriteButton
        alreadyFavorited={isFavorited}
        isBiggerListingCard
        onClick={handleFavoriteClick}
        listing={listing}
        size="36px"
      />
    </div>
  );

  const badges = [];

  if (active && recencyTime.daysDiff < 5) {
    badges.push(
      <div key="new" className={getInfoBadgeCX()} data-name="InfoBadge">
        New {recencyTime.displayDiff} ago
      </div>,
    );
  }

  if (hasSpecialOffers) {
    const badgeType = isSpecialOffersExperiment ? 'specialOfferLabel' : 'upsell';
    const badgeText = isSpecialOffersExperiment && specialOfferLabel ? specialOfferLabel : 'Special offer';
    badges.push(
      <div key="special-offer" className={getInfoBadgeCX(badgeType)} data-name="InfoBadge" aria-label="special offer">
        {badgeText}
      </div>,
    );
  }

  // Priority logic: Price drop > New unit (never show both)
  const showPriceDropBadge = priceDropCount > 0 && !isPriceDropControl;
  const showNewUnitBadge = !showPriceDropBadge && newUnitCount > 0 && !isNewUnitsControl;

  if (showPriceDropBadge) {
    badges.push(<PriceDropBadge key="price-drops" text="Price drop" variant="srp" showTooltip={false} />);
  }

  if (showNewUnitBadge) {
    const unitText = newUnitCount === 1 ? 'new unit' : 'new units';
    badges.push(
      <NewUnitBadge key="new-units" text={`${newUnitCount} ${unitText}`} variant="srp" showTooltip={false} />,
    );
  }

  if (rentalApplicationStatus === 'accepted') {
    badges.push(
      <div
        key="applications"
        className={getInfoBadgeCX('upsell')}
        data-name="InfoBadge"
        aria-label="accepts applications"
      >
        {isApplyInstantlyExperiment ? 'Apply instantly' : 'Accepts applications'}
      </div>,
    );
  }

  if (incomeRestricted) {
    badges.push(
      <div
        key="income-restricted"
        className={getInfoBadgeCX('housing')}
        data-name="InfoBadge"
        aria-label="income restricted"
      >
        Income restricted
      </div>,
    );
  }

  if (studentHousing) {
    badges.push(
      <div
        key="student-housing"
        className={getInfoBadgeCX('housing')}
        data-name="InfoBadge"
        aria-label="student housing"
      >
        Student housing
      </div>,
    );
  }

  if (seniorHousing) {
    badges.push(
      <div key="senior-housing" className={getInfoBadgeCX('housing')} data-name="InfoBadge" aria-label="senior housing">
        Senior housing
      </div>,
    );
  }

  // Limit to 2 badges if isCarouselCard is true
  const displayBadges = isCarouselCard ? badges.slice(0, 2) : badges;

  const PhotosComponent = (
    <figure className={getHeroPhotoContainerCX({ isCarouselCard })} data-name="HeroPhotoContainer">
      {FavoriteButtonComponent}
      {medPhotoUrls?.length > 0 && (
        <Carousel
          displayName={displayName}
          handleListingClick={handleListingClick}
          photoUrls={medPhotoUrls}
          isCarouselCard={isCarouselCard}
          highPriority={highPriority}
          shouldLazyLoadListingCardPhotos={shouldLazyLoadListingCardPhotos}
        />
      )}
      {medPhotoUrls?.length === 0 && (
        <img
          style={{ height: '100%' }}
          src={noPhoto}
          onClick={handleListingClick}
          alt=""
          {...(isCarouselCard && { width: 298, height: 224 })}
        />
      )}
      <div className={badgeContainerCX} data-name="BadgeContainer" onClick={handleListingClick}>
        {displayBadges}
      </div>
    </figure>
  );

  return (
    <article
      className={getListingCardContainerCX({ listingViewed, isCarouselCard, shouldUseNewLayout })}
      data-name="ListingCardContainer"
    >
      <SafeDeferredRender
        mode={
          shouldDeferRender
            ? RenderMode.RENDER_SERVER_SIDE_HYDRATE_ON_VISIBLE
            : RenderMode.RENDER_SERVER_SIDE_HYDRATE_ON_IMMEDIATE
        }
        visibilityOptions={{ rootMargin: '150px' }}
      >
        {shouldLazyLoadListingCardPhotos ? (
          <LazyLoader
            height="auto"
            width="auto"
            customPlaceholder={<figure className={getHeroPhotoContainerCX()} data-name="HeroPhotoContainerLazyLoad" />}
          >
            {PhotosComponent}
          </LazyLoader>
        ) : (
          PhotosComponent
        )}
        <section
          className={getDetailedInfoContainerCX({ shouldUseNewLayout })}
          data-name="DetailedInfoContainer"
          onClick={handleListingClick}
        >
          <div className={infoBadgesCX} data-name="InfoBadges">
            {trusted && (
              <div className={badgeOutlineCX} data-name="BadgeOutline">
                <img
                  src={IconVerified}
                  height="14px"
                  width="14px"
                  alt="This is a verified listing"
                  title="This is a verified listing"
                />
              </div>
            )}
            {has3dTour && (
              <div className={badgeOutlineCX} data-name="BadgeOutline">
                <img
                  src={Icon3DGlasses}
                  height="12px"
                  width="16px"
                  alt="This listing has a 3D tour available"
                  title="This listing has a 3D tour available"
                />
              </div>
            )}
          </div>
          <div className={detailedInfoTextCX} data-name="DetailedInfoText" onClick={handleListingClick}>
            {(() => {
              // Use the shouldUseNewLayout variable calculated earlier (based on card width < 400px)

              if (shouldUseNewLayout) {
                // 5-line layout for cards < 400px: Price -> Badge -> Beds/Baths -> Name -> Location
                return (
                  <>
                    <div
                      className={priceTextCX}
                      data-name="PriceText"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {listingMinMaxPriceBeds.priceDisplayRange}
                    </div>
                    <div>
                      <TotalMonthlyPriceBadge
                        listing={listing}
                        iconVariant="default"
                        size="sm"
                        data-testid="listing-price-tooltip"
                      />
                    </div>
                    <p style={{ fontWeight: 'bold' }}>
                      {listingMinMaxPriceBeds.bedDisplaySemiCompact}
                      {listingMinMaxPriceBeds.bathDisplaySemiCompact && (
                        <span>, {listingMinMaxPriceBeds.bathDisplaySemiCompact}</span>
                      )}
                      {modelTypeUnitCount
                        ? ` | ${stringHelper.pluralize(modelTypeUnitCount, 'unit', 'units')} available`
                        : ''}
                    </p>
                    <a
                      aria-label={`See more details about ${address?.hideAddress ? 'this listing' : displayName}`}
                      className={listingCardAnchorCX}
                      data-name="ListingCardAnchor"
                      href={listing.uriV2}
                      onClick={() => {
                        handleListingClick(null, false);
                      }}
                    >
                      <h3 style={{ display: 'inline' }}>{displayName}</h3>
                    </a>
                    <span>{displayBrokerInfo ? ` | ${brokerName}` : ''}</span>
                    <p>{`${formattedPropertyType} in ${`${address.city}, ${address.state}`}`}</p>
                  </>
                );
              } else {
                // Original 4-line layout for cards >= 400px: Price+Badge -> Beds/Baths -> Name -> Location
                return (
                  <>
                    <div
                      className={priceTextCX}
                      data-name="PriceText"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {listingMinMaxPriceBeds.priceDisplayRange}
                      <TotalMonthlyPriceBadge
                        listing={listing}
                        iconVariant="default"
                        size="sm"
                        data-testid="listing-price-tooltip"
                      />
                    </div>
                    <p style={{ fontWeight: 'bold' }}>
                      {listingMinMaxPriceBeds.bedDisplaySemiCompact}
                      {listingMinMaxPriceBeds.bathDisplaySemiCompact && (
                        <span>, {listingMinMaxPriceBeds.bathDisplaySemiCompact}</span>
                      )}
                      {modelTypeUnitCount
                        ? ` | ${stringHelper.pluralize(modelTypeUnitCount, 'unit', 'units')} available`
                        : ''}
                    </p>
                    <a
                      aria-label={`See more details about ${address?.hideAddress ? 'this listing' : displayName}`}
                      className={listingCardAnchorCX}
                      data-name="ListingCardAnchor"
                      href={listing.uriV2}
                      onClick={() => {
                        handleListingClick(null, false);
                      }}
                    >
                      <h3 style={{ display: 'inline' }}>{displayName}</h3>
                    </a>
                    <span>{displayBrokerInfo ? ` | ${brokerName}` : ''}</span>
                    <p>{`${formattedPropertyType} in ${`${address.city}, ${address.state}`}`}</p>
                  </>
                );
              }
            })()}
          </div>
        </section>
      </SafeDeferredRender>
    </article>
  );
};

export { SkeletonCard };
export default ListingCardV3;
